import { sumBalances, sumByCategory } from "../collectionUtils";
import {
  calculateCurrentRatio,
  calculateDaysWorkingCapital,
  calculateDefensiveInterval,
  calculateFreeCashFlow,
  calculateNetCashFlow,
  calculateOperatingCashFlow,
  calculateQuickRatio,
} from "../liquidityMetrics";

export const getRatioStatus = (ratio, excellentThreshold, goodThreshold) => {
  if (ratio >= excellentThreshold) return "ممتاز";
  if (ratio >= goodThreshold) return "جيد";
  if (ratio >= 1) return "مقبول";
  return "يحتاج تحسين";
};

export const getPercentageStatus = (percentage, excellentThreshold, goodThreshold) => {
  if (percentage >= excellentThreshold) return "ممتاز";
  if (percentage >= goodThreshold) return "جيد";
  if (percentage > 0) return "مقبول";
  return "سلبي";
};

export const generateAlerts = (balanceData, cashFlowData, pnlData) => {
  const alerts = [];
  const currentRatio = calculateCurrentRatio(balanceData);
  if (currentRatio < 1.5) {
    alerts.push({ type: "تحذير", message: "نسبة التداول منخفضة", description: `النسبة الحالية ${currentRatio.toFixed(2)} أقل من 1.5، قد تواجه صعوبة في سداد الالتزامات قصيرة الأجل` });
  }

  const revenue = sumByCategory(pnlData.revenue || []);
  const netIncome = revenue - sumByCategory(pnlData.expenses || []);
  const netMargin = revenue > 0 ? (netIncome / revenue) * 100 : 0;
  if (netMargin < 10) {
    alerts.push({ type: "تحذير", message: "هامش الربحية الصافي منخفض", description: `هامش الربحية الصافي ${netMargin.toFixed(2)}% أقل من المستوى المثالي 10%` });
  }

  if (calculateOperatingCashFlow(cashFlowData) < 0) {
    alerts.push({ type: "خطر", message: "تدفق نقدي تشغيلي سلبي", description: "التدفق النقدي التشغيلي سلبي، قد يكون هناك مشكلة في النشاط التشغيلي الأساسي" });
  }

  const totalAssets = sumBalances(balanceData.assets);
  const totalLiabilities = sumBalances(balanceData.liabilities);
  const debtRatio = totalAssets > 0 ? totalLiabilities / totalAssets : 0;
  if (debtRatio > 0.7) {
    alerts.push({ type: "تحذير", message: "نسبة المديونية مرتفعة", description: `نسبة المديونية ${(debtRatio * 100).toFixed(2)}% أعلى من 70%، قد تكون المخاطر المالية مرتفعة` });
  }

  return alerts;
};

export const projectNextPeriod = (balanceData, cashFlowData, pnlData) => {
  const revenue = sumByCategory(pnlData.revenue || []);
  const expenses = sumByCategory(pnlData.expenses || []);
  const projectedRevenue = revenue * 1.1;
  const projectedExpenses = expenses * 1.05;
  const projectedNetIncome = projectedRevenue - projectedExpenses;

  return {
    projectedRevenue,
    projectedExpenses,
    projectedNetIncome,
    projectedCashFlow: calculateOperatingCashFlow(cashFlowData) * 1.08,
    growthRate: {
      revenue: 10,
      expenses: 5,
      netIncome: ((projectedNetIncome - (revenue - expenses)) / ((revenue - expenses) || 1)) * 100,
    },
  };
};

export const generateRecommendations = (balanceData, cashFlowData, pnlData) => {
  const recommendations = [];
  if (calculateCurrentRatio(balanceData) < 2) recommendations.push({ priority: "عالي", action: "تحسين السيولة", details: "زيادة الأصول المتداولة أو تقليل الخصوم المتداولة لتحسين نسبة التداول" });

  const revenue = sumByCategory(pnlData.revenue || []);
  const expenses = sumByCategory(pnlData.expenses || []);
  const netMargin = revenue > 0 ? ((revenue - expenses) / revenue) * 100 : 0;
  if (netMargin < 15) recommendations.push({ priority: "متوسط", action: "تحسين الربحية", details: "مراجعة هيكل التكاليف والبحث عن فرص زيادة الإيرادات" });
  if (calculateOperatingCashFlow(cashFlowData) < expenses * 0.5) recommendations.push({ priority: "عالي", action: "تحسين التدفق النقدي", details: "تحصيل الذمم المدينة بشكل أسرع أو تأجيل الذمم الدائنة" });
  return recommendations;
};

export const buildGeneralSnapshot = (balanceData, cashFlowData, pnlData) => ({
  totalAssets: sumBalances(balanceData.assets),
  totalLiabilities: sumBalances(balanceData.liabilities),
  totalEquity: sumBalances(balanceData.equity),
  totalRevenue: sumByCategory(pnlData.revenue || []),
  totalExpenses: sumByCategory(pnlData.expenses || []),
  operatingCashFlow: calculateOperatingCashFlow(cashFlowData),
  freeCashFlow: calculateFreeCashFlow(cashFlowData),
  netCashFlow: calculateNetCashFlow(cashFlowData),
  currentRatio: calculateCurrentRatio(balanceData),
  quickRatio: calculateQuickRatio(balanceData),
  daysWorkingCapital: calculateDaysWorkingCapital(balanceData, cashFlowData),
  defensiveInterval: calculateDefensiveInterval(balanceData, cashFlowData),
});
